import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import CursoService from '../../services/curso.service';
import { useTranslation } from '../../hooks/useTranslation';

const CursoForm = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  useEffect(() => {
    if (isEdit) {
      fetchCurso();
    }
  }, [id, isEdit]);

  const fetchCurso = async () => {
    setLoading(true);
    try {
      const response = await CursoService.get(id);
      const curso = response.data?.data;
      setFormData({
        name: curso.name || '',
      });
    } catch (error) {
      console.error('Error loading curso:', error);
      showSnackbar(t('cursos.errorLoadingCurso'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      showSnackbar(t('cursos.pleaseFillRequired'), 'error');
      return;
    }

    try {
      setLoading(true);
      if (isEdit) {
        await CursoService.update(id, formData);
        showSnackbar(t('cursos.cursoUpdatedSuccess'));
      } else {
        await CursoService.create(formData);
        showSnackbar(t('cursos.cursoCreatedSuccess'));
      }
      navigate('/cursos');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          (error.response?.data?.errors ? 
                            Object.values(error.response.data.errors).flat().join(', ') : 
                            `Error ${isEdit ? 'updating' : 'creating'} curso`);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => navigate('/cursos')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5">
              {isEdit ? t('cursos.editCurso') : t('cursos.newCurso')}
            </Typography>
          </Box>
        }
        action={
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={loading}
          >
            {isEdit ? t('cursos.updateCurso') : t('cursos.saveCurso')}
          </Button>
        }
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('cursos.cursoName')}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </Grid>
        </Grid>
      </CardContent>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default CursoForm;
